import { CircularProgress } from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";

import { useSaveSeed, useSeed } from "@/api/creativesphere";
import { compositionCodec } from "@/api/creativesphere/codecs/composition";
import { CompositionPresetBuilder } from "@/features/GenLab/components/CompositionPresetBuilder";
import { describeComposition } from "@/features/GenLab/components/compositionPresets";

export const Route = createFileRoute(
  "/_auth/genlab/$siteId/presets/composition/edit/$presetId",
)({
  component: EditCompositionPresetRoute,
});

function EditCompositionPresetRoute() {
  const { siteId, presetId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useSeed(siteId, presetId);
  const saveSeed = useSaveSeed(siteId);

  const goBackToStudio = () => {
    void navigate({ to: "/genlab/$siteId", params: { siteId } });
  };

  useEffect(() => {
    if (isError) goBackToStudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  if (isLoading) return <CircularProgress />;
  if (!data) return null;

  const preset = compositionCodec.fromSeed(data);

  const handleSave = (changes: {
    name: string;
    format: string;
    placement: string;
  }) => {
    saveSeed.mutate(
      {
        slug: preset.id,
        body: {
          seed_type: compositionCodec.seedType,
          name: changes.name,
          description: describeComposition({
            format: changes.format,
            placement: changes.placement,
          }),
          params: compositionCodec.toParams({
            format: changes.format,
            placement: changes.placement,
          }),
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar("Information saved", { variant: "success" });
          goBackToStudio();
        },
        onError: () => {
          enqueueSnackbar("Couldn't save changes. Please try again.", {
            variant: "error",
          });
        },
      },
    );
  };

  return (
    <CompositionPresetBuilder
      preset={preset}
      onSave={handleSave}
      onBack={goBackToStudio}
    />
  );
}
